/**
 * ProjectionChartsAccordion.tsx – the three charts at the top of the year-by-year window: money in and out,
 * what they own, what they owe.
 *
 * A three-way chooser above ONE chart (owner-approved mock-up, 25 Oct 2026). It replaced three tall blue strips
 * with their labels set upright down the side, which took a quarter of the width on a phone and read as
 * CASHFLOW / NET WORTH / DEBT — words a family member has to translate. The chooser gives the chart its width
 * back and says what each chart is in plain words.
 *
 * The chooser's choice can be held by the window (`pane` + `onPaneChange`) so the window's own heading can name
 * the chart on show; left alone, it holds its own.
 *
 * One chart is mounted at a time: each measures its own container to lay itself out, so one rendered inside a
 * hidden box would measure zero width and never re-measure. The three are three configurations of one component
 * (ProjectionChart): same zoom/pan, hover tooltips and legend toggles in each.
 */
import React, { useState } from 'react';
import ProjectionChart from '../../components/charts/ProjectionChart.tsx';
import { CHART_STATUS_CRITICAL, type ChartPoint, type ChartSeries } from '../../components/charts/chartTypes.ts';
import { CASHFLOW_INCOME_SERIES } from './cashflowChartData.ts';
import { DEBT_SERIES, NET_WORTH_SERIES } from './balanceSheetChartData.ts';
import type { ChartReference } from '../../components/charts/ProjectionChart.tsx';

export type ProjectionPaneId = 'cashflow' | 'networth' | 'debt';

interface ProjectionChartsAccordionProps {
    cashflow: ChartPoint[];
    netWorth: ChartPoint[];
    debt: ChartPoint[];
    /** Full currency formatter (the window's, so the today's-dollars toggle applies). */
    formatValue: (value: number) => string;
    /** The chart on show, when the window holds the choice. */
    pane?: ProjectionPaneId;
    onPaneChange?: (pane: ProjectionPaneId) => void;
}

interface PaneConfig {
    id: ProjectionPaneId;
    /** The chooser's label: what the reader is choosing between. */
    label: string;
    /** The window's heading while this chart is on show. */
    heading: string;
    /** Chart name as it reads inside a control label — "Zoom in to {name} chart". */
    name: string;
    title: string;
    subtitle: string;
    ariaDescription: string;
    series: readonly ChartSeries[];
    stackLabel: string;
    stackTotalLabel: string;
    reference?: ChartReference;
    emptyMessage: string;
}

export const PROJECTION_PANES: PaneConfig[] = [
    {
        id: 'cashflow',
        label: 'Money in and out',
        heading: 'Money in and out, each year',
        name: 'money in and out',
        title: 'What comes in, and what each year costs',
        subtitle: 'The bars are money coming in; the dotted line is what each year costs. Red is the part paid from savings.',
        ariaDescription:
            'stacked income bars against a dotted line for what each year costs, with the part paid from savings shown in red. The table below lists the same figures.',
        series: CASHFLOW_INCOME_SERIES,
        stackLabel: 'Income',
        stackTotalLabel: 'Total income',
        reference: { id: 'expenses', label: 'What the year costs', gap: { id: 'shortfall', label: 'Paid from savings', clearedLabel: 'Left over', color: CHART_STATUS_CRITICAL } },
        emptyMessage: 'No income or costs to chart for this projection.',
    },
    {
        id: 'networth',
        label: 'What they own',
        heading: 'What they own, each year',
        name: 'what they own',
        title: 'What they own at the end of each year',
        subtitle: 'By kind of asset. Hover a year for what is owed against it, and what is left once that is taken off.',
        ariaDescription:
            'stacked bars of what is owned at the end of each projected year, by kind of asset. Hovering a year gives the debt owed against it and the resulting net worth. The table below lists the same figures.',
        series: NET_WORTH_SERIES,
        stackLabel: 'Assets',
        stackTotalLabel: 'Total owned',
        emptyMessage: 'No assets to chart for this projection.',
    },
    {
        id: 'debt',
        label: 'What they owe',
        heading: 'What they owe, each year',
        name: 'what they owe',
        title: 'What they owe at the end of each year',
        subtitle: 'Loans and other borrowing, by kind, and whether they grow or run down.',
        ariaDescription: 'stacked bars of what is owed at the end of each projected year, by kind of debt.',
        series: DEBT_SERIES,
        stackLabel: 'Debt',
        stackTotalLabel: 'Total owed',
        emptyMessage: 'This projection carries no loans or other debt.',
    },
];

const ProjectionChartsAccordion = ({ cashflow, netWorth, debt, formatValue, pane, onPaneChange }: ProjectionChartsAccordionProps) => {
    const [ownPane, setOwnPane] = useState<ProjectionPaneId>('cashflow');
    const openPane = pane ?? ownPane;
    const selectPane = (id: ProjectionPaneId) => {
        setOwnPane(id);
        onPaneChange?.(id);
    };
    const pointsFor: Record<ProjectionPaneId, ChartPoint[]> = { cashflow, networth: netWorth, debt };

    if (cashflow.length === 0) return null;
    const open = PROJECTION_PANES.find((p) => p.id === openPane) ?? PROJECTION_PANES[0]!;

    return (
        <div className="space-y-3" data-testid="projection-charts-accordion">
            <div role="tablist" aria-label="Which chart to show" className="flex w-full overflow-hidden rounded-lg border border-slate-300 bg-white sm:inline-flex sm:w-auto">
                {PROJECTION_PANES.map((p, i) => {
                    const isOpen = p.id === openPane;
                    return (
                        <button
                            key={p.id}
                            type="button"
                            role="tab"
                            aria-selected={isOpen}
                            aria-controls="projection-chart-pane"
                            id={`projection-chart-tab-${p.id}`}
                            data-testid={`projection-chart-tab-${p.id}`}
                            onClick={() => selectPane(p.id)}
                            className={`flex-1 px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-blue-600 sm:flex-none ${i > 0 ? 'border-l border-slate-300' : ''} ${
                                isOpen ? 'bg-brand-navy text-white' : 'text-slate-700 hover:bg-slate-50'
                            }`}
                        >
                            {p.label}
                        </button>
                    );
                })}
            </div>
            <div id="projection-chart-pane" role="tabpanel" aria-labelledby={`projection-chart-tab-${open.id}`} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <ProjectionChart
                    key={open.id}
                    chartId={open.id}
                    name={open.name}
                    title={open.title}
                    subtitle={open.subtitle}
                    ariaDescription={open.ariaDescription}
                    points={pointsFor[open.id]}
                    series={open.series}
                    stackLabel={open.stackLabel}
                    stackTotalLabel={open.stackTotalLabel}
                    yAxisLabel={open.id === 'cashflow' ? 'Dollars per year' : 'Dollars'}
                    xAxisLabel="Year"
                    reference={open.reference}
                    formatValue={formatValue}
                    emptyMessage={open.emptyMessage}
                />
            </div>
        </div>
    );
};

export default ProjectionChartsAccordion;
